import { __awaiter } from '../../../node_modules/tslib/tslib.es6.js';
import { jsx, jsxs } from 'react/jsx-runtime';
import { RotateCw } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { getHeadersWithUniqueVisitorId } from '../../../Auth/index.js';
import moment from 'moment';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from '../../../Components/ui/drawer.js';

// Helper function to format timestamps using moment.js
const formatTimestamp = (timestamp) => {
    const m = moment(timestamp);
    const now = moment();
    const diffInMinutes = now.diff(m, "minutes");
    const diffInHours = now.diff(m, "hours");
    const diffInDays = now.diff(m, "days");
    if (diffInMinutes < 1) {
        return "Just now";
    }
    else if (diffInMinutes < 60) {
        return `${diffInMinutes}m ago`;
    }
    else if (diffInHours < 24) {
        return `${diffInHours}h ago`;
    }
    else if (diffInDays === 1) {
        return "Yesterday";
    }
    else if (diffInDays < 7) {
        return `${diffInDays}d ago`;
    }
    else {
        // Format as "Jan 15" or "Jan 15, 2024" if not current year
        const formatString = m.year() === now.year() ? "MMM D" : "MMM D, YYYY";
        return m.format(formatString);
    }
};
// API function to fetch conversations
const fetchConversations = () => __awaiter(void 0, void 0, void 0, function* () {
    // Get authentication headers including JWT token (if signed in) and visitor ID
    const headers = yield getHeadersWithUniqueVisitorId();
    const response = yield fetch("/api-rewards/concierge/conversations", {
        method: "GET",
        headers: Object.assign({ "Content-Type": "application/json" }, headers),
    });
    if (!response.ok) {
        throw new Error("Failed to fetch conversations");
    }
    return response.json();
});
const ConversationHistoryDrawer = ({ open, onClose, onConversationSelect }) => {
    const { data, isLoading, isFetching, error, refetch } = useQuery({
        queryKey: ["conversations"],
        queryFn: fetchConversations,
        enabled: open,
        staleTime: 5 * 60 * 1000,
        gcTime: 10 * 60 * 1000, // 10 minutes (renamed from cacheTime in v5)
    });
    const handleConversationClick = (conversationId) => {
        onConversationSelect === null || onConversationSelect === void 0 ? void 0 : onConversationSelect(conversationId);
        onClose();
    };
    return (jsx(Drawer, Object.assign({ open: open, onOpenChange: (isOpen) => !isOpen && onClose(), direction: "left" }, { children: jsx(DrawerContent, Object.assign({ className: "tw:h-full tw:w-80 tw:rounded-none" }, { children: jsxs("div", Object.assign({ className: "tw:flex tw:h-full tw:flex-col tw:bg-white" }, { children: [jsxs(DrawerHeader, Object.assign({ className: "tw:flex tw:flex-row tw:items-center tw:justify-between tw:px-3 tw:py-3 tw:space-y-0" }, { children: [jsx(DrawerTitle, Object.assign({ className: "tw:text-sm tw:font-semibold" }, { children: "Chats" })), jsx("button", Object.assign({ onClick: () => refetch(), disabled: isFetching, "aria-label": "Refresh conversations", className: "tw:inline-flex tw:h-8 tw:w-8 tw:items-center tw:justify-center tw:rounded-lg tw:text-gray-600 hover:tw:bg-gray-100 hover:tw:text-gray-900 tw:transition-colors disabled:tw:opacity-50 tw:cursor-pointer" }, { children: jsx(RotateCw, { size: 16, strokeWidth: 2, className: isFetching ? "tw:animate-spin" : "" }) }))] })), jsxs("div", Object.assign({ className: "tw:flex-1 tw:overflow-y-auto" }, { children: [isLoading && (jsx("div", Object.assign({ className: "tw:flex tw:items-center tw:justify-center tw:p-4" }, { children: jsx("div", { className: "tw:h-5 tw:w-5 tw:animate-spin tw:rounded-full tw:border-2 tw:border-gray-300 tw:border-t-gray-900" }) }))), error && (jsx("div", Object.assign({ className: "tw:mx-3 tw:my-2 tw:rounded-lg tw:bg-red-50 tw:p-3 tw:text-sm tw:text-red-800" }, { children: "Failed to load conversations. Please try again." }))), data && data.conversations.length === 0 && (jsx("div", Object.assign({ className: "tw:p-6 tw:text-center tw:text-sm tw:text-gray-500" }, { children: "No conversations yet. Start a new conversation to see your history here." }))), data && data.conversations.length > 0 && (jsx("div", { children: data.conversations.map((conversation) => (jsxs("button", Object.assign({ onClick: () => handleConversationClick(conversation.conversationId), className: "tw:w-full tw:px-3 tw:py-2.5 tw:text-left tw:transition-colors tw:group tw:cursor-pointer", style: {
                                        backgroundColor: "transparent",
                                    }, onMouseEnter: (e) => {
                                        e.currentTarget.style.backgroundColor = "#f9fafb";
                                    }, onMouseLeave: (e) => {
                                        e.currentTarget.style.backgroundColor = "transparent";
                                    } }, { children: [jsx("div", Object.assign({ className: "tw:line-clamp-2 tw:text-sm tw:font-medium tw:text-gray-900" }, { children: conversation.title || conversation.preview })), jsx("div", Object.assign({ className: "tw:mt-1 tw:text-xs tw:text-gray-500" }, { children: formatTimestamp(conversation.lastUpdated) }))] }), conversation.conversationId))) }))] }))] })) })) })));
};

export { ConversationHistoryDrawer };
//# sourceMappingURL=index.js.map
